import React from 'react';
import { Box, Collapse } from '@mui/material';
import { PatientUploadedMedicalDocument } from '@uplift/api';
import { Modal, ModalActions, ModalContent, ModalTitle } from '../../../../../Modal';
import { Button } from '../../../../../Button';
import { Alert } from '../../../../../Alert';
import { DocumentDropzone, DropzoneErrorCode } from './DocumentDropzone.component';
import { DocumentDetailsForm } from './DocumentDetailsForm.component';


export type UploadDocumentsModalProps = {
  patientId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: (doc: PatientUploadedMedicalDocument) => Promise<void>;
};


export const UploadDocumentsModal: React.VFC<UploadDocumentsModalProps> = (props) => {
  const [ error, setError ] = React.useState<DropzoneErrorCode | null>(null);
  const [ file, setFile ] = React.useState<File | null>(null);

  const content = React.useMemo(() => {
    if (!file) {
      const errorMessageByCode: Partial<Record<DropzoneErrorCode, string>> = {
        [DropzoneErrorCode.FileTooLarge]: "PDFs must be less than 10MB. Try reducing your PDF's file size or choose another file to upload.",
        [DropzoneErrorCode.FileInvalidType]: 'Only PDFs can be uploaded. Try converting the document to a PDF or choose another file to upload.',
        [DropzoneErrorCode.TooManyFiles]: 'Only one document can be uploaded at once.',
      };

      return (
        <>
          <ModalTitle>Select document to upload</ModalTitle>
          <ModalContent>
            <Collapse in={!!error}>
              <Box pb={2}>
                <Alert severity="error">
                  {error ? errorMessageByCode[error] : error}
                </Alert>
              </Box>
            </Collapse>
            <Box height="280px">
              {!file && (
                <DocumentDropzone
                  onFiles={files => {
                    setError(null);
                    setFile(files[0]);
                  }}
                  onError={setError}
                />
              )}
            </Box>
          </ModalContent>
          <ModalActions>
            <Button onClick={props.onClose}>Cancel</Button>
          </ModalActions>
        </>
      );
    }

    return (
      <DocumentDetailsForm
        patientId={props.patientId}
        file={file}
        onCancel={() => setFile(null)}
        onSuccess={props.onSuccess}
      />
    );
  }, [ file, error ]);

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      fullWidth
      maxWidth="md"
      showCloseIcon={false}
      slotProps={{
        paper: { style: { maxWidth: '600px' } },
        transition: {
          onExited: () => {
            setFile(null);
            setError(null);
          }
        }
      }}
    >
      {content}
    </Modal>
  );
};
